"use strict";
import { PROTOCOL_LEGEND_ELEMENT_ID } from "components/Charts/types";
import { formatTickMarks } from "components/Charts/utils";
import { MissingDataBars } from "components/Table/icons";
import { useOnClickOutside } from "hooks/useOnClickOutside";
import { atom } from "jotai";
import { useUpdateAtom } from "jotai/utils";
import { useTheme } from "lib/styled-components";
import {
  CrosshairMode,
  LineStyle,
  createChart
} from "lightweight-charts";
import { useEffect, useMemo, useRef, useState } from "react";
import { Trans } from "react-i18next";
import { ThemedText } from "theme/components";
import { Flex, assertWebElement, styled, useMedia } from "ui/src";
import { useLocalizationContext } from "uniswap/src/features/language/LocalizationContext";
import { useCurrentLocale } from "uniswap/src/features/language/hooks";
import { NumberType } from "utilities/src/format/types";
import { v4 as uuidv4 } from "uuid";
export const refitChartContentAtom = atom(void 0);
export const DEFAULT_TOP_PRICE_SCALE_MARGIN = 0.32;
export const DEFAULT_BOTTOM_PRICE_SCALE_MARGIN = 0.15;
export class ChartModel {
  api;
  data;
  chartDiv;
  onCrosshairMove;
  _hoverData;
  _lastTooltipWidth = null;
  tooltipId = `chart-tooltip-${uuidv4()}`;
  constructor(chartDiv, params) {
    this.chartDiv = chartDiv;
    this.onCrosshairMove = params.onCrosshairMove;
    this.data = params.data;
    this.api = createChart(chartDiv);
    this.api.subscribeCrosshairMove((param) => {
      let newHoverData = void 0;
      const logical = param.logical;
      const x = param.point?.x;
      const y = param.point?.y;
      if (x !== void 0 && isBetween(x, 0, this.chartDiv.clientWidth) && y !== void 0 && isBetween(y, 0, this.chartDiv.clientHeight) && logical !== void 0) {
        const item = param.seriesData.get(this.series);
        if (item) {
          newHoverData = { item, x, y, logicalIndex: logical };
        }
      }
      const prevHoverData = this._hoverData;
      if (newHoverData?.item.time !== prevHoverData?.item.time || newHoverData?.logicalIndex !== prevHoverData?.logicalIndex || newHoverData?.x !== prevHoverData?.x || newHoverData?.y !== prevHoverData?.y) {
        this._hoverData = newHoverData;
        this.onSeriesHover(newHoverData);
      }
    });
  }
  /**
   * Updates React state with the current crosshair data.
   * This method should be overridden in subclasses to provide specific hover functionality.
   * When overriding, call `super.onSeriesHover(data)` to maintain base functionality.
   */
  onSeriesHover(hoverData) {
    this.onCrosshairMove?.(hoverData?.item, hoverData?.logicalIndex);
    if (!hoverData) {
      return;
    }
    const x = hoverData.x + this.api.priceScale("left").width() + 10;
    const deadzoneWidth = this._lastTooltipWidth ? Math.ceil(this._lastTooltipWidth) : 45;
    const xAdjusted = Math.min(x, this.api.paneSize().width - deadzoneWidth);
    const transformX = `calc(${xAdjusted}px)`;
    const y = hoverData.y;
    const flip = y <= 20 + 100;
    const extraOffset = y < 50 && hoverData.x < 100 ? 10 : 0;
    const yPx = y + (flip ? 1 : -1) * (20 + extraOffset);
    const yPct = flip ? "" : " - 100%";
    const transformY = `calc(${yPx}px${yPct})`;
    const tooltip = document.getElementById(this.tooltipId);
    const legend = document.getElementById(PROTOCOL_LEGEND_ELEMENT_ID);
    if (tooltip) {
      tooltip.style.transform = `translate(${transformX}, ${transformY})`;
      const tooltipMeasurement = tooltip.getBoundingClientRect();
      this._lastTooltipWidth = tooltipMeasurement.width || null;
    }
    if (legend) {
      legend.style.left = `${x}px`;
      const heroWidth = 230;
      if (x < heroWidth) {
        legend.style.top = "80px";
      } else {
        legend.style.top = "unset";
      }
      const transformOffset = 60;
      const maxXOffset = this.api.paneSize().width - 40;
      if (x < transformOffset) {
        legend.style.transform = `translateX(-${x - 4}%)`;
      } else if (x > maxXOffset) {
        legend.style.transform = `translateX(-${transformOffset + (x - maxXOffset)}%)`;
      } else {
        legend.style.transform = `translateX(-${transformOffset}%)`;
      }
    }
  }
  /** Updates the chart without re-creating it or resetting pan/zoom. */
  updateOptions({ locale, theme, format, isLargeScreen, onCrosshairMove }, nonDefaultChartOptions) {
    this.onCrosshairMove = onCrosshairMove;
    const defaultOptions = {
      localization: {
        locale,
        priceFormatter: (price) => format.convertFiatAmountFormatted(price, NumberType.FiatTokenPrice)
      },
      autoSize: true,
      layout: { textColor: theme.neutral2, background: { color: "transparent" } },
      timeScale: {
        tickMarkFormatter: formatTickMarks,
        borderVisible: false,
        ticksVisible: false,
        timeVisible: true,
        fixLeftEdge: true,
        fixRightEdge: true
      },
      rightPriceScale: {
        visible: isLargeScreen,
        borderVisible: false,
        scaleMargins: {
          top: DEFAULT_TOP_PRICE_SCALE_MARGIN,
          bottom: DEFAULT_BOTTOM_PRICE_SCALE_MARGIN
        },
        autoScale: true
      },
      grid: {
        vertLines: {
          visible: false
        },
        horzLines: {
          visible: false
        }
      },
      crosshair: {
        horzLine: {
          visible: true,
          style: LineStyle.Solid,
          width: 1,
          color: theme.surface3,
          labelVisible: false
        },
        mode: CrosshairMode.Magnet,
        vertLine: {
          visible: true,
          style: LineStyle.Solid,
          width: 1,
          color: theme.surface3,
          labelVisible: false
        }
      },
      // Enable scrolling vertically when onTouchMove is enabled on mobile devices (when chart is not focused)
      handleScroll: {
        horzTouchDrag: true,
        vertTouchDrag: false
      }
    };
    this.api.applyOptions({ ...defaultOptions, ...nonDefaultChartOptions });
  }
  /** Updates visible range to fit all data from all series. */
  fitContent() {
    this.api.timeScale().fitContent();
  }
  /** Removes the injected canvas from the chartDiv. */
  remove() {
    this.api.remove();
  }
}
function isBetween(num, lower, upper) {
  return num > lower && num < upper;
}
export function Chart({
  Model,
  TooltipBody,
  params,
  height,
  children,
  className,
  disableChartTouchPanning
}) {
  const setRefitChartContent = useUpdateAtom(refitChartContentAtom);
  const [chartDivElement, setChartDivElement] = useState(null);
  const [crosshairData, setCrosshairData] = useState(void 0);
  const format = useLocalizationContext();
  const theme = useTheme();
  const locale = useCurrentLocale();
  const media = useMedia();
  const isLargeScreen = !media.lg;
  const modelParams = useMemo(
    () => ({ ...params, format, theme, locale, isLargeScreen, onCrosshairMove: setCrosshairData }),
    [format, isLargeScreen, locale, params, theme]
  );
  const chartModelRef = useRef();
  useEffect(() => {
    if (chartDivElement && chartModelRef.current === void 0) {
      assertWebElement(chartDivElement);
      chartModelRef.current = new Model(chartDivElement, modelParams);
      setRefitChartContent(() => () => chartModelRef.current?.fitContent());
    }
  }, [Model, chartDivElement, modelParams, setRefitChartContent]);
  useEffect(() => {
    chartModelRef.current?.updateOptions(modelParams);
  }, [modelParams]);
  useEffect(() => {
    return () => {
      chartModelRef.current?.remove();
      chartModelRef.current = void 0;
      setRefitChartContent(void 0);
    };
  }, [setRefitChartContent]);
  useOnClickOutside({
    node: { current: chartDivElement },
    handler: () => setCrosshairData(void 0)
  });
  const touchMoveHandler = disableChartTouchPanning ? (e) => e.stopPropagation() : void 0;
  return <Flex
    width="100%"
    position="relative"
    animation="fast"
    ref={setChartDivElement}
    height={height}
    className={className}
    onTouchMove={touchMoveHandler}
  >{children && children(crosshairData)}{TooltipBody && crosshairData && <ChartTooltip id={chartModelRef.current?.tooltipId} includeBorder={!params.hideTooltipBorder}><TooltipBody data={crosshairData} /></ChartTooltip>}{params.stale && <StaleBanner />}</Flex>;
}
const ChartTooltip = styled(Flex, {
  alignItems: "center",
  position: "absolute",
  left: 0,
  top: 0,
  zIndex: "$tooltip",
  borderWidth: 0,
  borderStyle: "solid",
  pointerEvents: "none",
  // Prevent tooltip from interfering with mouse events
  variants: {
    includeBorder: {
      true: {
        backgroundColor: "$surface5",
        backdropFilter: "blur(8px)",
        borderRadius: "$rounded8",
        borderColor: "$surface3",
        borderWidth: 1,
        p: "$spacing8"
      }
    }
  }
});
const StaleBannerWrapper = styled(ChartTooltip, {
  borderRadius: "$rounded16",
  left: "unset",
  top: "unset",
  right: "$spacing12",
  bottom: "$spacing40",
  p: "$spacing12",
  backgroundColor: "$surface4"
});
function StaleBanner() {
  const theme = useTheme();
  return <StaleBannerWrapper data-testid="chart-stale-banner"><Flex row gap="$gap8"><MissingDataBars color={theme.neutral1} /><ThemedText.BodySmall><Trans i18nKey="common.dataOutdated" /></ThemedText.BodySmall></Flex></StaleBannerWrapper>;
}
